import { useState } from "react";
import T from "../theme";
import { GS } from "../components/ui/GlobalStyles";
import { Icon } from "../components/ui/Icon";
import { Card } from "../components/ui/Card";
import { Btn } from "../components/ui/Btn";
import { Field, inp } from "../components/ui/Field";
import { supabase } from "../lib/supabase";
import { fetchProfile, recoverPassword as recoverPasswordApi } from "../lib/db";
import logoEscuro from "../assets/logo_escuro.svg";
import logoBranco from "../assets/logo_branco.svg";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ─── Shared logo header ─────────────────────────────────────────────────── */
const LogoHeader = ({ dark }) => (
  <div style={{ textAlign: "center", marginBottom: 28 }}>
    <img src={dark ? logoEscuro : logoBranco} alt="VillaGest" style={{ height: 38, width: "auto", display: "block", margin: "0 auto 10px" }} />
    <p style={{ color: T.textMuted, fontSize: 14 }}>Gestão de Condomínio</p>
  </div>
);

/* ─── Dark mode toggle (top-right corner) ────────────────────────────────── */
const DarkToggle = ({ dark, onToggleDark }) => (
  <button
    onClick={onToggleDark}
    title={dark ? "Modo Claro" : "Modo Escuro"}
    style={{ position: "absolute", top: 18, right: 20, display: "flex", alignItems: "center", gap: 6, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: T.textMuted, fontSize: 13, fontWeight: 500, boxShadow: T.shadow }}>
    <Icon name={dark ? "sun" : "moon"} size={14} color={T.textMuted} />
    {dark ? "Modo Claro" : "Modo Escuro"}
  </button>
);

/* ─── Alert box ──────────────────────────────────────────────────────────── */
const Alert = ({ msg, type = "error" }) => {
  const cfg = {
    error:   { bg: T.dangerLight,  border: T.dangerBorder,  color: T.dangerText,  icon: "alert" },
    success: { bg: T.successLight, border: T.successBorder, color: T.successText, icon: "check" },
    info:    { bg: T.primaryLight, border: T.primaryBorder, color: T.primary, icon: "alert" },
  }[type];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 12px", background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: T.radius, color: cfg.color, fontSize: 13, marginBottom: 14 }}>
      <Icon name={cfg.icon} size={14} color={cfg.color} />{msg}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════ */
export const Login = ({ onLogin, dark, onToggleDark, onBack }) => {
  // "login" | "recover" | "recoverSent"
  const [mode, setMode]             = useState("login");

  /* ── Login state ── */
  const [email, setEmail]           = useState("");
  const [pass, setPass]             = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [err, setErr]               = useState("");
  const [fe, setFe]                 = useState({ email: "", pass: "" });
  const [loading, setLoading]       = useState(false);

  /* ── Recover state ── */
  const [recEmail, setRecEmail]     = useState("");
  const [recErr, setRecErr]         = useState("");
  const [recLoading, setRecLoading] = useState(false);

  /* ─── Login submit ─────────────────────────────────────────────────────── */
  const go = async () => {
    const e = {
      email: !email.trim() ? "O email é obrigatório." : "",
      pass:  !pass.trim()  ? "A password é obrigatória." : "",
    };
    setFe(e);
    if (e.email || e.pass) return;

    setLoading(true);
    setErr("");

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: pass,
      });

      if (error) {
        if (error.message.toLowerCase().includes("invalid login credentials") ||
            error.message.toLowerCase().includes("invalid")) {
          setErr("Email ou password incorretos.");
        } else if (error.message.toLowerCase().includes("email not confirmed")) {
          setErr("Email não confirmado. Verifique a sua caixa de correio.");
        } else {
          setErr(error.message);
        }
        return;
      }

      // Load profile from DB
      const profile = await fetchProfile(data.user.id);

      if (!profile.active) {
        await supabase.auth.signOut();
        setErr("O acesso a esta conta está desativado. Contacte o administrador.");
        return;
      }

      onLogin(profile);
    } catch (ex) {
      setErr("Erro de ligação. Tente novamente.");
      console.error(ex);
    } finally {
      setLoading(false);
    }
  };

  /* ─── Recover password ─────────────────────────────────────────────────── */
  const recoverPassword = async () => {
    if (!recEmail.trim()) { setRecErr("Introduza o seu endereço de email."); return; }
    if (!EMAIL_RE.test(recEmail.trim())) { setRecErr("Email inválido."); return; }
    setRecErr("");
    setRecLoading(true);
    try {
      await recoverPasswordApi(recEmail.trim());
      setMode("recoverSent");
    } catch (ex) {
      setRecErr("Erro de ligação. Tente novamente.");
    } finally {
      setRecLoading(false);
    }
  };

  /* ─── Back to login ────────────────────────────────────────────────────── */
  const backToLogin = () => {
    setMode("login");
    setErr(""); setFe({ email: "", pass: "" });
    setRecEmail(""); setRecErr("");
  };

  /* ════════════════════════════════════════════════════════════════════════ */
  /* ── RECOVER SENT screen ── */
  if (mode === "recoverSent") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative" }}>
        <GS />
        <DarkToggle dark={dark} onToggleDark={onToggleDark} />
        <div style={{ width: "100%", maxWidth: 420, animation: "fadeIn 0.4s ease" }}>
          <LogoHeader dark={dark} />
          <Card style={{ padding: "28px 26px", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: T.successLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="mail" size={24} color={T.success} />
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: T.text, marginBottom: 8 }}>Email enviado</h2>
            <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Se existir uma conta associada a <strong>{recEmail}</strong>, receberá um email com instruções para recuperar a sua password.
            </p>
            <Btn variant="secondary" onClick={backToLogin} style={{ width: "100%", justifyContent: "center" }}>
              Voltar ao login
            </Btn>
          </Card>
        </div>
      </div>
    );
  }

  /* ── RECOVER screen ── */
  if (mode === "recover") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative" }}>
        <GS />
        <DarkToggle dark={dark} onToggleDark={onToggleDark} />
        <div style={{ width: "100%", maxWidth: 420, animation: "fadeIn 0.4s ease" }}>
          <LogoHeader dark={dark} />
          <Card style={{ padding: "28px 26px" }}>
            <button onClick={backToLogin} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: 13, padding: 0, marginBottom: 16 }}>
              ← Voltar
            </button>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Recuperar password</h2>
            <p style={{ color: T.textMuted, fontSize: 13, marginBottom: 20 }}>
              Introduza o seu email e enviaremos instruções para repor a password.
            </p>

            {recErr && <Alert msg={recErr} />}

            <Field label="Email">
              <input
                value={recEmail}
                onChange={e => { setRecEmail(e.target.value); setRecErr(""); }}
                onKeyDown={e => e.key === "Enter" && recoverPassword()}
                type="email"
                placeholder="email@exemplo.pt"
                style={inp(recErr)}
                onFocus={e => e.target.style.border = `1px solid ${T.primary}`}
                onBlur={e => e.target.style.border = `1px solid ${recErr ? T.danger : T.border}`}
              />
            </Field>

            <Btn onClick={recoverPassword} disabled={recLoading} style={{ width: "100%", justifyContent: "center", padding: "11px", fontSize: 15 }}>
              {recLoading ? "A enviar..." : "Enviar instruções"}
            </Btn>
          </Card>
        </div>
      </div>
    );
  }

  /* ── LOGIN screen (default) ── */
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative" }}>
      <GS />
      <DarkToggle dark={dark} onToggleDark={onToggleDark} />
      <div style={{ width: "100%", maxWidth: 400, animation: "fadeIn 0.4s ease" }}>
        {onBack && (
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: 13, padding: 0, marginBottom: 14 }}>
            ← Voltar ao início
          </button>
        )}
        <LogoHeader dark={dark} />

        <Card style={{ padding: "28px 26px" }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Iniciar sessão</h2>
          <p style={{ color: T.textMuted, fontSize: 13, marginBottom: 22 }}>Introduza as suas credenciais para aceder.</p>

          <Field label="Email" error={fe.email}>
            <input
              value={email}
              onChange={e => { setEmail(e.target.value); setFe(f => ({ ...f, email: "" })); setErr(""); }}
              type="email"
              placeholder="email@exemplo.pt"
              style={inp(fe.email)}
              onFocus={e => e.target.style.border = `1px solid ${T.primary}`}
              onBlur={e => e.target.style.border = `1px solid ${fe.email ? T.danger : T.border}`}
            />
          </Field>

          <Field label="Password" error={fe.pass}>
            <div style={{ position: "relative" }}>
              <input
                value={pass}
                onChange={e => { setPass(e.target.value); setFe(f => ({ ...f, pass: "" })); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && go()}
                type={showPass ? "text" : "password"}
                placeholder="••••••••"
                style={{ ...inp(fe.pass), paddingRight: 40 }}
                onFocus={e => e.target.style.border = `1px solid ${T.primary}`}
                onBlur={e => e.target.style.border = `1px solid ${fe.pass ? T.danger : T.border}`}
              />
              <button
                onClick={() => setShowPass(s => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex" }}>
                <Icon name={showPass ? "eyeOff" : "eye"} size={16} />
              </button>
            </div>
          </Field>

          {/* Recuperar password link */}
          <div style={{ textAlign: "right", marginTop: -8, marginBottom: 16 }}>
            <button
              onClick={() => setMode("recover")}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.primary, fontSize: 13, padding: 0, fontWeight: 500 }}>
              Esqueceu a password?
            </button>
          </div>

          {err && <Alert msg={err} />}

          <Btn onClick={go} disabled={loading} style={{ width: "100%", justifyContent: "center", padding: "11px", fontSize: 15 }}>
            {loading ? "A entrar..." : "Entrar"}
          </Btn>

        </Card>
      </div>
    </div>
  );
};
